/*
 * Genovare V2 product page.
 * 1. Gallery: the thumbnails (box, product card) swap the main image with a fade. They stay hidden until this
 *    script runs, so nobody sees buttons that do nothing.
 * 2. Lightbox: links with data-gv-lightbox open the matching <dialog>. Without JavaScript they open the image itself.
 * 3. "On this page" links: the link for the section in view is marked.
 * 4. Certificate viewer: pages sit side by side in a scroll-snap strip, so touch swipe and trackpads work natively;
 *    the arrow buttons and the Left/Right keys step one page, and the counter follows.
 */
(function () {
  'use strict';

  function setupGallery() {
    var gallery = document.querySelector('[data-gv-gallery]');
    if (!gallery) return;
    var thumbs = gallery.querySelector('[data-gv-thumbs]');
    if (!thumbs) return;
    var buttons = Array.prototype.slice.call(thumbs.querySelectorAll('[data-gv-thumb]'));
    var slides = Array.prototype.slice.call(gallery.querySelectorAll('[data-gv-slide]'));
    if (buttons.length < 2) return;

    function show(key, focus) {
      slides.forEach(function (slide) {
        var on = slide.getAttribute('data-gv-slide') === key;
        slide.hidden = !on;
        slide.classList.remove('is-fading');
        if (on) {
          void slide.offsetWidth;
          slide.classList.add('is-fading');
        }
      });
      buttons.forEach(function (button) {
        var on = button.getAttribute('data-gv-thumb') === key;
        button.setAttribute('aria-selected', on ? 'true' : 'false');
        button.tabIndex = on ? 0 : -1;
        if (on && focus) button.focus();
      });
    }

    buttons.forEach(function (button, index) {
      button.addEventListener('click', function () {
        show(button.getAttribute('data-gv-thumb'), false);
      });
      button.addEventListener('keydown', function (event) {
        var next = null;
        if (event.key === 'ArrowRight') next = buttons[(index + 1) % buttons.length];
        if (event.key === 'ArrowLeft') next = buttons[(index + buttons.length - 1) % buttons.length];
        if (event.key === 'Home') next = buttons[0];
        if (event.key === 'End') next = buttons[buttons.length - 1];
        if (next) {
          event.preventDefault();
          show(next.getAttribute('data-gv-thumb'), true);
        }
      });
    });
    thumbs.hidden = false;
  }

  function setupLightbox() {
    document.addEventListener('click', function (event) {
      var close = event.target.closest('[data-gv-lightbox-close]');
      if (close) {
        var open = close.closest('dialog');
        if (open) closeDialog(open);
        return;
      }
      var link = event.target.closest('[data-gv-lightbox]');
      if (link) {
        var dialog = document.getElementById(link.getAttribute('data-gv-lightbox'));
        if (!dialog) return;
        event.preventDefault();
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        document.documentElement.classList.add('gv-dialog-open');
        dialog.dispatchEvent(new CustomEvent('gv:open'));
        return;
      }
      // A click on the dark backdrop closes it.
      if (event.target.classList && event.target.classList.contains('gv-lightbox')) closeDialog(event.target);
    });
    document.addEventListener(
      'close',
      function (event) {
        if (event.target.classList && event.target.classList.contains('gv-lightbox')) {
          document.documentElement.classList.remove('gv-dialog-open');
        }
      },
      true
    );
  }

  function closeDialog(dialog) {
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    document.documentElement.classList.remove('gv-dialog-open');
  }

  function setupCertViewer() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-gv-certview]'), function (view) {
      var track = view.querySelector('[data-gv-certview-track]');
      var pages = Array.prototype.slice.call(view.querySelectorAll('[data-gv-certview-page]'));
      var prev = view.querySelector('[data-gv-certview-prev]');
      var next = view.querySelector('[data-gv-certview-next]');
      var count = view.querySelector('[data-gv-certview-count]');
      var dialog = view.closest('dialog');
      if (!track || !pages.length) return;
      var template = (count && count.getAttribute('data-template')) || '';
      var current = -1;
      var queued = false;

      function index() {
        var width = track.clientWidth || 1;
        return Math.max(0, Math.min(pages.length - 1, Math.round(track.scrollLeft / width)));
      }
      function update() {
        queued = false;
        var i = index();
        if (i === current) return;
        current = i;
        if (count) count.textContent = template.replace('[page]', i + 1).replace('[total]', pages.length);
        // A button that becomes disabled would drop keyboard focus, so hand focus to the other one first.
        if (prev && next) {
          if (i === 0 && document.activeElement === prev) next.focus();
          if (i === pages.length - 1 && document.activeElement === next) prev.focus();
        }
        if (prev) prev.disabled = i === 0;
        if (next) next.disabled = i === pages.length - 1;
      }
      function go(i) {
        i = Math.max(0, Math.min(pages.length - 1, i));
        track.scrollTo({ left: i * track.clientWidth });
      }

      // When scrolling stops between two pages (some browsers snap loosely), finish the move to the nearest page.
      var settleTimer = null;
      function settle() {
        var target = index() * track.clientWidth;
        if (Math.abs(track.scrollLeft - target) > 1) track.scrollTo({ left: target });
      }
      var hasScrollEnd = 'onscrollend' in window;
      if (hasScrollEnd) track.addEventListener('scrollend', settle);

      track.addEventListener(
        'scroll',
        function () {
          if (!hasScrollEnd) {
            window.clearTimeout(settleTimer);
            settleTimer = window.setTimeout(settle, 160);
          }
          if (queued) return;
          queued = true;
          window.requestAnimationFrame(update);
        },
        { passive: true }
      );
      if (prev) prev.addEventListener('click', function () { go(index() - 1); });
      if (next) next.addEventListener('click', function () { go(index() + 1); });
      if (dialog) {
        // Listen on the document: if focus ever lands outside the viewer, the keys still turn the page.
        document.addEventListener('keydown', function (event) {
          if (!dialog.open) return;
          if (event.key === 'ArrowRight') { event.preventDefault(); go(index() + 1); }
          if (event.key === 'ArrowLeft') { event.preventDefault(); go(index() - 1); }
        });
        dialog.addEventListener('gv:open', function () {
          // Fetch every page once the viewer opens, so swiping never waits on a lazy image.
          pages.forEach(function (page) {
            var img = page.querySelector('img');
            if (img) img.loading = 'eager';
          });
          current = -1;
          update();
        });
      }
      update();
    });
  }

  function setupToc() {
    var toc = document.querySelector('[data-gv-toc]');
    if (!toc || !('IntersectionObserver' in window)) return;
    var links = Array.prototype.slice.call(toc.querySelectorAll('a[href^="#"]'));
    var targets = links
      .map(function (link) {
        return document.getElementById(link.getAttribute('href').slice(1));
      })
      .filter(Boolean);
    if (!targets.length) return;

    function mark(id) {
      links.forEach(function (link) {
        link.classList.toggle('is-active', link.getAttribute('href') === '#' + id);
      });
    }
    mark(targets[0].id);

    var visible = {};
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          visible[entry.target.id] = entry.isIntersecting;
        });
        for (var i = 0; i < targets.length; i++) {
          if (visible[targets[i].id]) return mark(targets[i].id);
        }
      },
      { rootMargin: '-30% 0px -55% 0px' }
    );
    targets.forEach(function (target) {
      io.observe(target);
    });
  }

  function init() {
    setupGallery();
    setupLightbox();
    setupCertViewer();
    setupToc();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
